import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Clarity from '@microsoft/clarity';
import './global.css';
import Hero from './components/Hero';
import KeepsakeCollections from './components/KeepsakeCollections';
import MagicBehindMoments from './components/MagicBehindMoments';
import BookingModule from './components/BookingModule';
import FAQ from './components/FAQ';
import { initParallax } from './utils/parallax';

const HomePage: React.FC = () => {
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const bookingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize parallax after component mounts
    initParallax();
  }, []);

  const handlePackageSelect = (packageId: string) => {
    setSelectedPackageId(packageId);
    // Smooth scroll to booking module
    setTimeout(() => {
      if (bookingRef.current) {
        bookingRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  return (
    <div className="app">
      <Hero />
      <div className="main-container">
      
      <section className="section section-storytelling" id="storytelling">
        <div className="parallax-element" data-parallax-speed="0.3">
          <h2>The Unscripted Story of Their Littleness</h2>
        </div>
        <p>You're there for every moment. The sleepy morning cuddles, the wobbly first steps, the messy spaghetti smiles. You live in these memories. Let us be there to capture their magic, so you can stay fully present in the making of them.</p>
        
        <div className="parallax-images-container">
          <div 
            className="parallax-image parallax-image-1 parallax-element" 
            data-parallax-speed="0.3"
            style={{
              backgroundImage: 'url(https://pratham-public-bucket.s3.us-east-1.amazonaws.com/1755837323662~2.jpg)'
            }}
          />
          <div 
            className="parallax-image parallax-image-2 parallax-element" 
            data-parallax-speed="0.5"
            style={{
              backgroundImage: 'url(https://pratham-public-bucket.s3.us-east-1.amazonaws.com/1755580643741~2.jpg)'
            }}
          />
          <div 
            className="parallax-image parallax-image-3 parallax-element" 
            data-parallax-speed="0.7"
            style={{
              backgroundImage: 'url(https://pratham-public-bucket.s3.us-east-1.amazonaws.com/1755580301621~2.jpg)'
            }}
          />
        </div>
      </section>
      
      <KeepsakeCollections onPackageSelect={handlePackageSelect} />
      
      <MagicBehindMoments />
      
      <section className="section section-privacy" id="privacy">
        <h2 className="parallax-element" data-parallax-speed="0.3">Protecting What's Precious</h2>
        
        <div className="privacy-promises">
          <div className="privacy-promise">
            <div className="promise-icon">
              <svg className="icon-audio-privacy" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="3"/>
                <path d="M30 25 C30 15, 40 10, 50 15 C60 10, 70 15, 70 25 L70 50 C70 65, 60 75, 50 75 C40 75, 30 65, 30 50 Z" fill="none" stroke="currentColor" strokeWidth="3"/>
                <line x1="20" y1="20" x2="80" y2="80" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                <circle cx="20" cy="20" r="3" fill="currentColor"/>
                <circle cx="80" cy="80" r="3" fill="currentColor"/>
              </svg>
            </div>
            <h3>Audio Privacy</h3>
            <p>We only capture smiles, not sounds. For your family's privacy, audio is never recorded.</p>
          </div>

          <div className="privacy-promise">
            <div className="promise-icon">
              <svg className="icon-data-security" viewBox="0 0 100 100">
                <rect x="25" y="45" width="50" height="35" rx="5" fill="none" stroke="currentColor" strokeWidth="3"/>
                <path d="M35 45 L35 35 C35 25, 42 20, 50 20 C58 20, 65 25, 65 35 L65 45" fill="none" stroke="currentColor" strokeWidth="3"/>
                <circle cx="50" cy="62" r="4" fill="currentColor"/>
                <line x1="50" y1="66" x2="50" y2="72" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>Data Security</h3>
            <p>Your memories are yours alone. All photos are encrypted and stored on our secure servers, protected with industry-leading security measures.</p>
          </div>

          <div className="privacy-promise">
            <div className="promise-icon">
              <svg className="icon-user-control" viewBox="0 0 100 100">
                <rect x="30" y="25" width="25" height="30" rx="3" fill="none" stroke="currentColor" strokeWidth="2.5"/>
                <line x1="35" y1="35" x2="50" y2="35" stroke="currentColor" strokeWidth="1.5"/>
                <line x1="35" y1="40" x2="45" y2="40" stroke="currentColor" strokeWidth="1.5"/>
                <line x1="35" y1="45" x2="50" y2="45" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="60" y="65" width="15" height="20" rx="2" fill="none" stroke="currentColor" strokeWidth="2.5"/>
                <rect x="65" y="60" width="5" height="5" rx="1" fill="none" stroke="currentColor" strokeWidth="2"/>
                <line x1="62" y1="75" x2="73" y2="75" stroke="currentColor" strokeWidth="1.5"/>
                <line x1="62" y1="78" x2="73" y2="78" stroke="currentColor" strokeWidth="1.5"/>
                <line x1="45" y1="65" x2="65" y2="45" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                <polygon points="40,60 50,65 40,70" fill="currentColor"/>
              </svg>
            </div>
            <h3>User Control</h3>
            <p>You have complete control. You decide which photos to keep, and you can permanently delete anything you don't want, forever.</p>
          </div>
        </div>
      </section>
      
      <section className="section section-faq" id="faq">
        <div className="faq-container">
          <h2>Questions?</h2>
          <p>We're here to help make your memory-making experience seamless.</p>
          <a 
            href="/faq" 
            className="faq-link"
            onClick={() => Clarity.event('homepage_view_faq')}
          >
            View Frequently Asked Questions
          </a>
        </div>
      </section>
      
      <div ref={bookingRef}>
        <BookingModule selectedPackageId={selectedPackageId} />
      </div>
      </div>
    </div>
  );
};

function App() {
  useEffect(() => {
    // Initialize Microsoft Clarity
    Clarity.init('syo5rheva8');
  }, []);

  return (
    <Router>
      <div className="router-container">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/faq" element={<FAQ />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
